import { useState, type ReactNode } from "react";
import { Link } from "react-router";
import dayjs from "dayjs";
import { formatMoney, formatMoneyCompact } from "@/lib/format";
import { StagePill, SubjectPill } from "@/components/contractings/pills";
import type { SuretyCompanyProfile, SuretyWinnerProspect } from "@/types/surety";
import { Chip, EmptyNote, type ChipTone } from "./SuretyUI";
import { AddClientButton, AddToTeamButton, SecondaryLink } from "./SuretyActions";

type ChipSpec = [ChipTone, string];

/** Lo que dicen las fichas OECE y SUNAT de una empresa, como etiquetas. */
function profileChips(profile: SuretyCompanyProfile | null): ChipSpec[] {
  if (!profile) return [];
  const chips: ChipSpec[] = [];
  if (profile.maxCapacity && profile.freeCapacity !== null) {
    const free = profile.freeCapacity / profile.maxCapacity;
    chips.push([
      free < 0.2 ? "risk" : "ok",
      `Capacidad libre ${formatMoneyCompact(profile.freeCapacity)} · ${Math.round(free * 100)}%`,
    ]);
  }
  if (profile.activeSanctions > 0) chips.push(["urgent", "Sanción vigente"]);
  else if (profile.sanctions > 0) chips.push(["info", `${profile.sanctions} sanción histórica`]);
  else chips.push(["ok", "Sin sanciones"]);
  // Sin consultar no lleva etiqueta: no es lo mismo que "sin deuda".
  if (profile.coactiveDebtTotal)
    chips.push(["urgent", `Deuda coactiva ${formatMoneyCompact(profile.coactiveDebtTotal)}`]);
  else if (profile.coactiveDebtTotal === 0) chips.push(["ok", "Sin deuda coactiva"]);
  return chips;
}

/** Una fila de prospecto: quién es, por qué aparece, cuánto vale y qué hacer. */
function ProspectRow({
  title,
  badge,
  href,
  lines,
  chips,
  metricLabel,
  metric,
  metricSub,
  actions,
}: {
  title: string;
  /** Va pegado al título, como la etapa de la licitación. */
  badge?: ReactNode;
  href: string;
  lines: (string | null)[];
  chips: ChipSpec[];
  metricLabel: string;
  metric: string;
  metricSub?: string;
  actions: ReactNode;
}) {
  return (
    <article className="grid gap-4 border-b px-5 py-4 last:border-0 md:grid-cols-[minmax(0,1fr)_12rem_12rem] md:items-center">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          {/* Pestaña nueva: el Mercado se revisa de corrido, sin perder el lugar en la lista. */}
          <Link
            to={href}
            target="_blank"
            rel="noopener noreferrer"
            className="cursor-pointer text-base font-bold text-slate-900 hover:text-primary dark:text-slate-50"
          >
            {title}
          </Link>
          {badge}
        </div>
        {lines.filter(Boolean).map((line) => (
          <p key={line} className="text-xs text-slate-500">
            {line}
          </p>
        ))}
        <div className="mt-2 flex flex-wrap gap-1.5">
          {chips.map(([tone, text]) => (
            <Chip key={text} tone={tone}>
              {text}
            </Chip>
          ))}
        </div>
      </div>
      <div className="md:text-right">
        <p className="text-xs text-slate-500">{metricLabel}</p>
        <p className="text-xl font-extrabold text-slate-900 dark:text-slate-50">{metric}</p>
        {metricSub && <p className="text-xs text-slate-500">{metricSub}</p>}
      </div>
      <div className="flex flex-col gap-2">{actions}</div>
    </article>
  );
}

// De a cuántos se muestran: "Ganaron" puede traer cientos en un mes.
const PAGE_SIZE = 50;

/** "hace 5 min", "hace 3 h", "hace 2 días": en el día se cuenta en horas o minutos. */
function timeAgo(date: string): string {
  const minutes = Math.max(0, dayjs().diff(dayjs(date), "minute"));
  if (minutes < 60) return minutes <= 1 ? "hace un momento" : `hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.floor(hours / 24);
  return `hace ${days} ${days === 1 ? "día" : "días"}`;
}

/**
 * En qué va la licitación, con el color de su etapa como en el resto de la app:
 * verde si hay ganador vigente, ámbar si está apelada.
 */
function movementChip(row: SuretyWinnerProspect): ChipSpec {
  if (row.signedAt) return ["ok", `Firmó el ${dayjs(row.signedAt).format("DD MMM")}`];
  if (row.stage === "APELADO") return ["risk", `Apelada · ganó ${timeAgo(row.awardedAt)}`];
  // El SEACE no publica cuándo se consintió: lo que sabemos es cuándo lo vimos.
  return row.consentedAt
    ? ["ok", `Vista consentida ${timeAgo(row.consentedAt)}`]
    : ["ok", `Adjudicada ${timeAgo(row.awardedAt)}`];
}

/** Ganadores del mes, del último movimiento al más viejo. */
export function WinnerProspects({ rows }: { rows: SuretyWinnerProspect[] }) {
  const [shown, setShown] = useState(PAGE_SIZE);
  if (rows.length === 0) return <EmptyNote>No hay ganadores con este filtro.</EmptyNote>;
  return (
    <>
      {rows.slice(0, shown).map((row) => (
        <ProspectRow
          key={`${row.contractingId}-${row.winnerName}`}
          title={row.winnerName}
          badge={
            // Objeto y etapa, en el mismo orden que la cabecera de la ficha.
            <>
              <SubjectPill subject={row.subject} />
              <StagePill stage={row.stage} />
            </>
          }
          href={row.companyId ? `/companies/${row.companyId}` : `/contracting/${row.contractingId}`}
          lines={[
            [row.nomenclature, row.entity].filter(Boolean).join(" · "),
            [
              `Buena pro el ${dayjs(row.awardedAt).format("DD MMM")}`,
              row.consentedAt ? `consentida (vista el ${dayjs(row.consentedAt).format("DD MMM")})` : null,
              // Sin el dato del OECE sale 0: mejor no decir nada que decir "0 registradas".
              row.registered > 0 ? `${row.registered} empresas registradas` : null,
            ]
              .filter(Boolean)
              .join(" · "),
            row.members.length > 0
              ? `Integrantes: ${row.members.map((m) => (m.isClient ? `${m.name} (tu cliente)` : m.name)).join(", ")}`
              : null,
          ]}
          chips={[
            movementChip(row),
            ...(row.isClient
              ? ([
                  [
                    "size",
                    row.isConsortium
                      ? `Tu cliente ${row.clientNames.join(" y ")} va en el consorcio`
                      : "Tu cliente",
                  ],
                ] as ChipSpec[])
              : []),
            ...(row.isConsortium ? ([["info", "Consorcio"]] as ChipSpec[]) : []),
            ...(row.partnerOf ? ([["warm", `Socio de tu cliente ${row.partnerOf}`]] as ChipSpec[]) : []),
            ...(row.likeClient ? ([["warm", `Perfil de ${row.likeClient}`]] as ChipSpec[]) : []),
            ...(row.isFrequentBidder ? ([["info", `${row.offers6m} ofertas en 6 meses`]] as ChipSpec[]) : []),
            ...profileChips(row.profile),
          ]}
          metricLabel="Monto adjudicado"
          metric={formatMoney(row.amount)}
          actions={
            <>
              <AddToTeamButton contractingId={row.contractingId} inPipeline={row.inPipeline} />
              {/* A un consorcio no se lo suma a la cartera: se suman sus integrantes. */}
              {row.winnerRuc && !row.isClient && <AddClientButton ruc={row.winnerRuc} />}
              <SecondaryLink to={`/contracting/${row.contractingId}`} newTab>
                Ver licitación
              </SecondaryLink>
            </>
          }
        />
      ))}
      {rows.length > shown && (
        <div className="px-5 py-4 text-center">
          <button
            type="button"
            onClick={() => setShown(shown + PAGE_SIZE)}
            className="cursor-pointer rounded-full border px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            Ver {Math.min(PAGE_SIZE, rows.length - shown)} más de {rows.length - shown}
          </button>
        </div>
      )}
    </>
  );
}
